import './converge-pdf-viewer.scss';

import { Alert, Collapse, Stack } from '@mui/material';
import {
  DocumentInput,
  DownloadFileQuery,
  useDownloadFile,
} from '@converge/graphql';
import { PropsWithChildren, lazy, useMemo } from 'react';

import { ApolloError } from '@apollo/client';
import { CircularLoader } from './circular-loader';
import { ConvergeButton } from './button';
import { ConvergeSectionTitle } from './converge-section-title';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { PropsOf } from '@emotion/react';
import { StackLayout } from './stack-layout';
import { base64ToPdfBlobUrl } from '../../domain/underwriting/utils';

const PDFViewerLazy = lazy(async () => {
  const { PDFViewer } = await import('@react-pdf/renderer');
  return { default: PDFViewer };
});

const PDFDownloadLinkLazy = lazy(async () => {
  const { PDFDownloadLink } = await import('@react-pdf/renderer');
  return { default: PDFDownloadLink };
});

export const ConvergePdfViewer = (props: PropsOf<typeof PDFViewerLazy>) => (
  <StackLayout flexDirection={'column'} flexGrow={1}>
    <StackLayout flexGrow={1} width={'100%'}>
      <PDFViewerLazy showToolbar height={'100%'} width={'100%'} {...props} />
    </StackLayout>
    <StackLayout paddingTop={2} width="100%">
      <PDFDownloadLinkLazy
        document={
          props.children as PropsOf<typeof PDFDownloadLinkLazy>['document']
        }
        fileName="converge-document.pdf"
      >
        {({ blob, url, loading, error }) => (
          <ConvergeButton
            loading={loading}
            label={loading ? 'Loading...' : 'Download'}
          />
        )}
      </PDFDownloadLinkLazy>
    </StackLayout>
  </StackLayout>
);

export interface IConvergePdfViewerNewProps {
  error?: ApolloError;
  loading: boolean;
  file?: Pick<DownloadFileQuery['file'], 'file'>;
  name: string;
}

const New = ({ error, loading, file, name }: IConvergePdfViewerNewProps) => {
  const pdfUrl = useMemo(() => {
    return base64ToPdfBlobUrl(file?.file);
  }, [file?.file]);

  return (
    <Stack>
      <ConvergeSectionTitle variant="h5" mb={1}>
        {name}
      </ConvergeSectionTitle>
      <Collapse
        in={loading}
        sx={{ height: '50vh', display: 'flex', alignItems: 'center' }}
        unmountOnExit
      >
        <CircularLoader style={{ color: ' #28c0a7' }} />
      </Collapse>

      {file ? (
        <iframe
          className="iframe-converge-pdf-viewer"
          src={pdfUrl}
          id="pdf"
          title={`${name} PDF`}
        ></iframe>
      ) : error ? (
        <Alert color="error" icon={<PriorityHighIcon />}>
          Error loading {name}: {error.message}
        </Alert>
      ) : !loading ? (
        <Alert color="error" icon={<PriorityHighIcon />}>
          Error loading {name}
        </Alert>
      ) : null}
    </Stack>
  );
};

ConvergePdfViewer.New = New;

export interface IConvergePdfViewerDownloadedFileProps extends DocumentInput {
  name: string;
}

ConvergePdfViewer.DownloadedFile = ({
  name,
  quoteUuid,
  submissionUuid,
  type,
  claimUuid,
  mttUuid,
}: IConvergePdfViewerDownloadedFileProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useDownloadFile(
    claimUuid
      ? {
          claimUuid,
          type,
        }
      : mttUuid
      ? {
          mttUuid,
          type,
        }
      : {
          quoteUuid,
          submissionUuid,
          type,
        }
  );

  return (
    <ConvergePdfViewer.New
      name={name}
      file={data?.file}
      loading={loading}
      error={error}
    />
  );
};

ConvergePdfViewer.Stack = ({
  children,
  hideTitle = false,
}: PropsWithChildren<{ hideTitle?: boolean }>) => {
  return (
    <Stack flexDirection={'column'} spacing={2}>
      {!hideTitle && (
        <ConvergeSectionTitle mb={0}>Documents</ConvergeSectionTitle>
      )}
      {children}
    </Stack>
  );
};
